/**
 * document-types.ts — typy współdzielone dla katalogu typów dokumentów.
 *
 * Część F2 planu „Globalne typy dokumentów — centralny katalog ITE z odczytem
 * na żywo u tenantów" (`docs/ADR/` — ADR do dopisania przy wdrożeniu backendu).
 *
 * Zakres tego pliku (frontend-foundations): same kontrakty danych, zero logiki.
 * `AdminGlobalDocumentType` — kształt zwracany przez `apps/api` panelowi ITE
 * (`/api/admin/global-document-types`, jeszcze nieistniejący — backend poza
 * zakresem tego etapu). `MergedDocumentTypeRow` — scalona lista typów lokalnych
 * i globalnych zwracana tenantowi przez `GET /api/document-types` (kontrakt
 * opisany w planie, sekcja „Kontrakt scalonej listy typów").
 */

import type { NumberingBaseCoreDateField } from './field-types';
import type { FilterConfig } from './list-view';

/** 7 klas dokumentów obsługiwanych przez obszar Dokumenty (docs/architecture/AUTH.md §22). */
export type DocumentClass =
  | 'purchase_invoice'
  | 'sale_invoice'
  | 'contract'
  | 'letter'
  | 'commercial'
  | 'internal'
  | 'specjalne';

/** Pochodzenie wiersza w scalonej liście typów dokumentów tenanta. */
export type DocumentTypeOrigin = 'global' | 'local';

/** Cykl życia typu globalnego: szkic → opublikowany → wycofany (bez hard-delete po publikacji). */
export type GlobalDocumentTypeStatus = 'draft' | 'published' | 'withdrawn';

/** Polityka resetu licznika numeracji dokumentu. */
export type DocumentNumberingReset = 'continuous' | 'yearly' | 'monthly';

/**
 * Typ globalny — kształt zwracany panelowi ITE przez `apps/api`
 * (`GET/POST /api/admin/global-document-types`).
 */
export interface AdminGlobalDocumentType {
  id: string;
  class: DocumentClass;
  code: string;
  name: string;
  description: string | null;
  numberingFormat: string | null;
  numberingBaseField: string | null;
  numberingReset: DocumentNumberingReset;
  status: GlobalDocumentTypeStatus;
  sortOrder: number;
  /**
   * Flaga „ten typ to wniosek o nieobecność" — typ wpisywany jako typ wniosku o nieobecność
   * (`tenant_settings.hr_absence_type_id`) NOWYM klientom przy zakładaniu. Istniejących klientów
   * nie zmienia — o ich typie decyduje wyłącznie ich ustawienie. Co najwyżej JEDEN oznaczony typ
   * w całej platformie; tylko klasy `HR_ABSENCE_DOCUMENT_CLASSES`.
   */
  isHrAbsenceSource: boolean;
  /**
   * Etap 3 (T3.5, layouty per typ dokumentu) — centralne wskazanie layoutu formularza
   * przez SLUG widoku z biblioteki ADM `kartoteka_views` (nie UUID — wiersze
   * zmaterializowane u tenanta różnią się per tenant). `null` = brak centralnego
   * wskazania (rezolucja `/layouts/active` krok 3 spada do kaskady, T3.6).
   */
  formViewSlug: string | null;
  /**
   * Centralny domyślny proces (ADR-0070) — UUID OPUBLIKOWANEGO szablonu procesu
   * z katalogu ADM `process_templates` (cel `crm_core.documents`), NIE proces
   * tenantowy bezpośrednio. `null` = brak centralnego wskazania (rezolucja u
   * tenanta: nadpisanie firmy → proces zlinkowany z tym szablonem → brak).
   */
  defaultProcessTemplateId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Wiersz scalonej listy typów dokumentów u tenanta (`GET /api/document-types`).
 * Łączy typy lokalne (per firma) i globalne (katalog ITE) z flagami przesłaniania
 * i dezaktywacji per firma — patrz plan §„Kontrakt scalonej listy typów".
 */
export interface MergedDocumentTypeRow {
  id: string;
  class: DocumentClass;
  code: string;
  name: string;
  description: string | null;
  numberingFormat: string | null;
  numberingReset: DocumentNumberingReset;
  origin: DocumentTypeOrigin;
  /** `null` dla typów lokalnych — status dotyczy tylko cyklu życia typu globalnego. */
  status: GlobalDocumentTypeStatus | null;
  /** Typ globalny przesłonięty przez aktywny lokalny typ o tym samym kodzie (case-insensitive). */
  shadowed: boolean;
  /** Typ globalny zdezaktywowany w tej firmie (nadpisanie tenantowe). */
  deactivatedInCompany: boolean;
  isActive: boolean;
  /** Czy wiersz może być wybrany w pickerze (uwzględnia status/shadowed/deactivatedInCompany). */
  selectable: boolean;
  /** Typ globalny jest read-only u tenanta — edycja tylko przez kopię lokalną. */
  readonly: boolean;
  companyId: string | null;
  /** Domyślny proces (obieg) — dla globalnych efektywny z nadpisania tenantowego. */
  defaultProcessId: string | null;
  /** Kolejność sortowania (źródłowa). */
  sortOrder: number;
  /** Pole-data numeracji (np. 'document_date'); null = domyślne. */
  numberingBaseField: string | null;
  /**
   * Layout formularza wskazywany przez typ — soft FK do `system_module_layouts`
   * (null = kaskada domyślna kartoteki). Dla wierszy globalnych efektywny
   * z tenantowego nadpisania per firma (`document_type_overrides.form_layout_id`,
   * Etap 2 T2.2) — analogicznie do `defaultProcessId`; bez nadpisania `null`.
   */
  formLayoutId: string | null;
  /** Znacznik soft-delete: null = aktywny, ISO string = skasowany. Globalne zawsze null. */
  gcrecord: string | null;
}

/**
 * Surowy wiersz `document_types` zwracany przez mutacje utwórz/edytuj/kopiuj
 * (NIE widok scalony — bez origin/selectable/shadowed/status).
 */
export interface DocumentTypeRecord {
  id: string;
  class: DocumentClass;
  companyId: string | null;
  name: string;
  code: string;
  description: string | null;
  numberingFormat: string | null;
  numberingBaseField: string | null;
  numberingReset: DocumentNumberingReset;
  defaultProcessId: string | null;
  /** Layout formularza wskazywany przez typ — soft FK do `system_module_layouts` (null = kaskada domyślna). */
  formLayoutId: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  gcrecord: string | null;
  gcrecordByUserId: string | null;
  gcrecordReason: string | null;
}

/**
 * Pole daty numeracji — pola BAZOWE/KLASOWE (isCore) każdej klasy dokumentu, które
 * mogą być podstawą numeru (`document_types.numbering_base_field`).
 *
 * Jedno źródło prawdy dla: (a) opcji edytora typu w panelu ITE (typy globalne —
 * brak pól dodatkowych), (b) walidacji dozwolonych core-slugów (tenant + ADM),
 * (c) mapowania core-slug → klucz w treści żądania (`payloadKey`) w resolverze numeracji.
 * Pola DODATKOWE (ext) klasy dokłada się dynamicznie z katalogu (`system_module_fields`),
 * poza tą stałą.
 *
 * SYNCHRONIZACJA: musi pokrywać się z polami `fieldType:'date'` w `coreFields`
 * kartotek dokumentów w `apps/api/lib/seed-system-modules.ts` (SEED_MODULES). Pilnuje
 * tego kolokowany test antydryfowy — przy dodaniu/zmianie pola daty w seedzie zaktualizuj
 * tę stałą (inaczej test padnie). `payloadKey` = klucz Zod w POST body danej klasy
 * (camelCase sluga). Pierwszy element każdej klasy = pole DOMYŚLNE (mapuje na
 * `documents.document_date`). Kształt pola wspólny z zadaniami (`NumberingBaseCoreDateField`).
 */
export type DocumentCoreDateField = Required<NumberingBaseCoreDateField>;

const DOC_BASE_FIELD_I18N = 'tenant.documentTypes.form.baseFieldOptions';

const DOCUMENT_DATE_FIELD: DocumentCoreDateField = {
  slug: 'document_date',
  payloadKey: 'documentDate',
  labelKey: `${DOC_BASE_FIELD_I18N}.documentDate`,
};

export const DOCUMENT_CORE_DATE_FIELDS: Record<DocumentClass, readonly DocumentCoreDateField[]> = {
  purchase_invoice: [
    { slug: 'issue_date', payloadKey: 'issueDate', labelKey: `${DOC_BASE_FIELD_I18N}.issueDate` },
    {
      slug: 'received_date',
      payloadKey: 'receivedDate',
      labelKey: `${DOC_BASE_FIELD_I18N}.receivedDate`,
    },
    { slug: 'sale_date', payloadKey: 'saleDate', labelKey: `${DOC_BASE_FIELD_I18N}.saleDate` },
    {
      slug: 'payment_due_date',
      payloadKey: 'paymentDueDate',
      labelKey: `${DOC_BASE_FIELD_I18N}.paymentDueDate`,
    },
    { slug: 'paid_at', payloadKey: 'paidAt', labelKey: `${DOC_BASE_FIELD_I18N}.paidAt` },
    // Ewidencja KSeF (0159) — pole jest na OBU klasach faktur (opisuje obecność faktury
    // w KSeF, nie stronę transakcji), dlatego powtarza się niżej w `sale_invoice`.
    // `ksef_submitted_at`/`ksef_available_at` świadomie POMINIĘTE mimo że są datami:
    // podstawą numeracji może być tylko data, którą klient PRZYSYŁA w żądaniu, a te dwie
    // są tylko do odczytu (wypełnia je import z KSeF albo nasza wysyłka) i nie ma ich
    // w schemacie zapisu.
    {
      slug: 'ksef_issue_date',
      payloadKey: 'ksefIssueDate',
      labelKey: `${DOC_BASE_FIELD_I18N}.ksefIssueDate`,
    },
  ],
  sale_invoice: [
    { slug: 'issue_date', payloadKey: 'issueDate', labelKey: `${DOC_BASE_FIELD_I18N}.issueDate` },
    { slug: 'sale_date', payloadKey: 'saleDate', labelKey: `${DOC_BASE_FIELD_I18N}.saleDate` },
    {
      slug: 'payment_due_date',
      payloadKey: 'paymentDueDate',
      labelKey: `${DOC_BASE_FIELD_I18N}.paymentDueDate`,
    },
    { slug: 'paid_at', payloadKey: 'paidAt', labelKey: `${DOC_BASE_FIELD_I18N}.paidAt` },
    // Ewidencja KSeF (0159) — lustro pola z faktur zakupu. Znaczników ewidencji ZAKUPU
    // sprzedaż nie dostaje (inne zbiory ustawowe), ale data wystawienia w KSeF jest wspólna.
    {
      slug: 'ksef_issue_date',
      payloadKey: 'ksefIssueDate',
      labelKey: `${DOC_BASE_FIELD_I18N}.ksefIssueDate`,
    },
  ],
  // ADR-0109b: okres umowy (satelita `document_contract_details`) — pola katalogu umów, więc też
  // kandydaci na podstawę numeracji (decyzja właściciela 2026-10-19). Etykiety = etykiety pól
  // formularza umowy (istniejące klucze). Umowa bezterminowa ma puste daty okresu — typ
  // numerowany po nich odrzuci zapis takiej umowy (NUMBERING_BASE_FIELD_REQUIRED).
  contract: [
    DOCUMENT_DATE_FIELD,
    {
      slug: 'period_from',
      payloadKey: 'periodFrom',
      labelKey: 'tenant.documents.contracts.detail.field.periodFrom',
    },
    {
      slug: 'period_to',
      payloadKey: 'periodTo',
      labelKey: 'tenant.documents.contracts.detail.field.periodTo',
    },
  ],
  letter: [DOCUMENT_DATE_FIELD],
  commercial: [DOCUMENT_DATE_FIELD],
  internal: [DOCUMENT_DATE_FIELD],
  specjalne: [DOCUMENT_DATE_FIELD],
};

/** Domyślny slug pola daty numeracji dla klasy (mapuje na `documents.document_date`). */
export function defaultNumberingBaseSlug(cls: DocumentClass): string {
  const first = DOCUMENT_CORE_DATE_FIELDS[cls][0];
  if (!first) throw new Error(`Brak core-pól daty dla klasy dokumentu: ${cls}`);
  return first.slug;
}

/** Zwraca definicję core-pola daty dla (klasa, slug) albo undefined, gdy to nie core-pole. */
export function findCoreDateField(
  cls: DocumentClass,
  slug: string,
): DocumentCoreDateField | undefined {
  return DOCUMENT_CORE_DATE_FIELDS[cls].find((f) => f.slug === slug);
}

/**
 * Czy `slug` jest dozwolonym polem daty numeracji dla klasy — core (stała) lub
 * pole dodatkowe (ext) typu data przekazane w `extDateSlugs`. Legacy `'document_date'`
 * dla klas faktur normalizuje się do sluga bazowego, więc też przechodzi.
 *
 * `extDateSlugs` — sluginy pól dodatkowych typu data/timestamp danej klasy: dla typów
 * tenanta z katalogu ext (`system_module_fields`), dla typów globalnych ITE z katalogu
 * CENTRALNEGO (`system_kartoteka_fields`, ADM) — patrz `resolveExtDateSlugs` /
 * `resolveCentralExtDateSlugs` w `lib/documents/numbering-base-field.ts`.
 * Walidacja serwerowa edytora typu (tenant + ADM) — patrz trasy `document-types`
 * i `admin/global-document-types`.
 */
export function isKnownNumberingBaseSlug(
  cls: DocumentClass,
  slug: string,
  extDateSlugs: readonly string[],
): boolean {
  const normalized = normalizeNumberingBaseSlug(cls, slug);
  if (findCoreDateField(cls, normalized) !== undefined) return true;
  return extDateSlugs.includes(normalized);
}

/**
 * Normalizuje zapisaną wartość `numbering_base_field` do realnego sluga.
 * `null`/puste → pole domyślne klasy. Legacy `'document_date'` zapisane dla klas faktur
 * (gdzie slug bazowy to `issue_date`, ta sama kolumna `documents.document_date`) → pole
 * domyślne klasy. W pozostałych przypadkach zwraca wartość bez zmian (może być slugiem
 * core lub ext — rozstrzyga resolver/walidator).
 */
export function normalizeNumberingBaseSlug(
  cls: DocumentClass,
  stored: string | null | undefined,
): string {
  if (stored == null || stored.trim() === '') return defaultNumberingBaseSlug(cls);
  if (stored === 'document_date' && !findCoreDateField(cls, 'document_date')) {
    return defaultNumberingBaseSlug(cls);
  }
  return stored;
}

// ── Typ wniosku o nieobecność (kadry) ──

/**
 * Klasy dokumentów, których typ może być „wnioskiem o nieobecność": oznaczenie ITE
 * (`global_document_types.is_hr_absence_source`) i ustawienie klienta
 * (`tenant_settings.hr_absence_type_id`) przyjmują WYŁĄCZNIE typy tych dwóch klas. Jedno źródło
 * prawdy dla walidacji serwera (panel ITE + ustawienie klienta), resolvera kadr i frontu
 * (przełącznik w oknie typu ITE, skrót „Nieobecności" → lista klasy).
 */
export const HR_ABSENCE_DOCUMENT_CLASSES = [
  'specjalne',
  'internal',
] as const satisfies readonly DocumentClass[];

/** Klasa dokumentu dopuszczona jako źródło wniosków o nieobecność. */
export type HrAbsenceDocumentClass = (typeof HR_ABSENCE_DOCUMENT_CLASSES)[number];

/** Czy klasa dokumentu może być źródłem wniosków o nieobecność. */
export function isHrAbsenceDocumentClass(cls: string): cls is HrAbsenceDocumentClass {
  return (HR_ABSENCE_DOCUMENT_CLASSES as readonly string[]).includes(cls);
}

/**
 * Obowiązujący u klienta typ wniosku o nieobecność — pole `hrAbsenceDocumentType`
 * odpowiedzi `GET /api/my/installed-modules` (skrót „Nieobecności" w menu). `null` w odpowiedzi
 * = typ nieznany (nie ustawiono, zapisany typ usunięty/wycofany/nieistniejący albo katalog
 * niedostępny) → skrótu nie ma.
 */
export interface HrAbsenceDocumentTypeRef {
  id: string;
  documentClass: HrAbsenceDocumentClass;
  /**
   * Obowiązujący typ to typ globalny oznaczony dziś przez ITE (typ lokalny — zawsze `false`) — skrót
   * prowadzi wtedy WYŁĄCZNIE do pozycji zainstalowanego modułu z tym typem; bez takiej pozycji
   * skrótu nie ma. W pozostałych przypadkach skrót prowadzi na listę klasy zawężoną do typu.
   */
  isGlobalDefault: boolean;
}

/**
 * Odpowiedź `GET /api/settings/hr-absence-type`. `typeId` = zapisane id typu wniosku
 * o nieobecność (`tenant_settings.hr_absence_type_id`) TAKIE, JAKIE JEST — także gdy typ przestał
 * obowiązywać (usunięty / wycofany; wtedy po prostu nie pasuje do żadnego wiersza listy typów);
 * `null` = żaden typ nie jest wnioskiem o nieobecność.
 */
export interface HrAbsenceTypeSettingResponse {
  typeId: string | null;
  /** Czy bieżąca osoba może zmienić ustawienie. */
  canManage: boolean;
}

/** Treść `PUT /api/settings/hr-absence-type` — id typu (globalnego albo lokalnego) albo `null`. */
export interface HrAbsenceTypeSettingUpdate {
  typeId: string | null;
}

/** Odpowiedź `PUT /api/settings/hr-absence-type` — zapisany stan. */
export type HrAbsenceTypeSettingUpdateResponse = HrAbsenceTypeSettingUpdate;

// ── Zawężanie listy dokumentów do wybranych typów (filtr na pozycji menu) ──

/**
 * Slug pola filtra „typ dokumentu" na pozycji menu (`MenuItem.view.baseFilters`)
 * i w edytorze filtrów stałych. Zawężenie listy dokumentów do wybranych typów
 * zapisuje się jako `{ field: DOCUMENT_TYPE_FILTER_FIELD, operator: 'in', value: <CSV id> }`.
 *
 * Ta sama nazwa co kolumna `documents.document_type_id` — dzięki temu backend
 * (deskryptor list dokumentów „bare-on-core" + trasy special/internal-docs) tłumaczy
 * ten filtr wprost na `inArray(documents.documentTypeId, …)`, bez wystawiania
 * `document_type_id` jako ogólnej kolumny sortowalnej/filtrowalnej silnika list.
 */
export const DOCUMENT_TYPE_FILTER_FIELD = 'document_type_id';

/**
 * Wyciąga zbiór id typów dokumentów z filtrów stałych pozycji menu — wartości
 * filtra `document_type_id in […]`. Konwencja wartości operatora `in`: CSV (string,
 * jak zapisuje `BaseFiltersEditor` i czyta `baseFiltersToHeaderFilters`) albo tablica
 * id. Inne kształty wartości (obiekt / liczba / boolean, element tablicy nie-string)
 * są IGNOROWANE — bez cichej koercji `String()` na fałszywe „id" typu „[object Object]"
 * (schema filtra ma `value: unknown`, więc kształtu nie gwarantuje żaden walidator).
 * Przycina białe znaki, pomija puste, deduplikuje. Kilka filtrów tego pola
 * (teoretyczne) łączy się w jeden zbiór.
 *
 * Zwraca `[]`, gdy nie ma filtra typu dokumentu — czyli brak zawężenia (lista pokazuje
 * wszystkie typy klasy). Uwzględniany jest WYŁĄCZNIE operator `in` (edytor filtrów
 * dla tego pola wymusza multi-select → `in`); inne operatory na tym polu są ignorowane.
 *
 * NIE waliduje formatu UUID: to robi granica URL (`parseDocumentTypeIds` w apps/api),
 * gdzie parametr bywa niezaufany. Tu walidujemy tylko KSZTAŁT wartości (string/tablica
 * stringów), nie format pojedynczego id.
 */
export function extractDocumentTypeIdsFromBaseFilters(
  baseFilters: FilterConfig[] | null | undefined,
): string[] {
  if (!baseFilters || baseFilters.length === 0) return [];
  const out = new Set<string>();
  for (const f of baseFilters) {
    if (f.field !== DOCUMENT_TYPE_FILTER_FIELD || f.operator !== 'in') continue;
    // Konwencja wartości `in`: CSV (string) lub tablica id. Inne kształty ignorujemy,
    // żeby nie wpuścić fałszywego „id" (np. "[object Object]" z koercji obiektu).
    const parts =
      typeof f.value === 'string'
        ? f.value.split(',')
        : Array.isArray(f.value)
          ? f.value.filter((v): v is string => typeof v === 'string')
          : [];
    for (const raw of parts) {
      const trimmed = raw.trim();
      if (trimmed.length > 0) out.add(trimmed);
    }
  }
  return [...out];
}
